import React from 'react';
import PropTypes from 'prop-types';
import { motion, AnimatePresence } from 'framer-motion';
import { Delete } from '@material-ui/icons';
import cx from 'classnames';
import PopupWrapper from '../PopupWrapper';
import Button from '../Button/Button';
import Input, { INPUT_TYPES } from '../Input/Input';
import ResourceStatePill from '../ResourceStatePill';
import { TIMEZONES, RESOURCE_STATES } from '../../constants';
import RadioTabs from '../RadioTabs/RadioTabs';
import TimeRow from './TimeRow';
import { OpeningHourHistory } from './OpeningHourHistory';

const OpeningHourCard = (props) => {
  const {
    deleteTemplate,
    isDeleted,
    onChange,
    onTemplateTypeChange,
    showDeleteWarningPopup,
    template,
    templateIndex,
    toggleDeleteWarningPopup,
    createNotification,
  } = props;
  return (
    <motion.li
      initial={{ opacity: 0, y: '-30%', x: '0%' }}
      animate={{ opacity: 1, y: '0%', x: '0%' }}
      exit={{ opacity: 0, y: '0%', x: '30%' }}
      transition={{
        ease: [0.18, 0.89, 0.39, 1.4],
        duration: 0.3,
      }}
      className="bg-white p-4 rounded shadow-md max-w-lg m-auto mb-10 relative">
      <div className="flex justify-between items-center mb-8">
        <div className="flex items-center">
          <ResourceStatePill state={template.state} />
          {isDeleted && (
            <div className="rounded-full text-xs px-2 leading-loose tracking-wide inline-block bg-lightRed ml-4">
              DELETED
            </div>
          )}
        </div>

        <OpeningHourHistory
          id={template.id}
          createNotification={createNotification}
        />

        {!isDeleted && (
          <PopupWrapper
            className="relative h-6"
            isOpen={showDeleteWarningPopup === templateIndex}
            togglePopup={toggleDeleteWarningPopup}>
            <Button
              type="transparent"
              className="p-0-important shadow-none-important h-6"
              onClick={() =>
                template.state === RESOURCE_STATES.DRAFT
                  ? toggleDeleteWarningPopup(templateIndex)
                  : deleteTemplate(templateIndex)
              }>
              <Delete className="text-darkGrey" />
            </Button>
            <AnimatePresence>
              {showDeleteWarningPopup === templateIndex && (
                <motion.div
                  initial={{ opacity: 0, x: '-70%', y: '0%' }}
                  animate={{ opacity: 1, x: '-102%' }}
                  exit={{ opacity: 0, x: '-70%' }}
                  transition={{
                    ease: [0.18, 0.89, 0.39, 1.4],
                    duration: 0.3,
                  }}
                  className="absolute pin-t pin-l px-2 py-4 bg-white shadow-lg rounded text-left w-64">
                  <div className="text-center leading-normal">
                    Are you sure you want to discard this draft?
                  </div>
                  <div className="flex justify-around mt-8">
                    <Button className="h-8" onClick={toggleDeleteWarningPopup}>
                      No
                    </Button>
                    <Button
                      type="red-transparent"
                      className="h-8"
                      onClick={() => deleteTemplate(templateIndex)}>
                      Yes, discard it
                    </Button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </PopupWrapper>
        )}
      </div>
      <div
        className={cx({
          'opacity-50 pointer-events-none': isDeleted,
        })}>
        <Input
          id={`${templateIndex}.name`}
          name={`${templateIndex}.name`}
          type={INPUT_TYPES.TEXT}
          placeholder="Enter temaplate name"
          value={template.name}
          onChange={onChange}
          inputClassName="shadow-inner bg-almostWhite"
          className="mb-4"
        />
        <div className="flex justify-between">
          <Input
            id={`${templateIndex}.time_zone`}
            name={`${templateIndex}.time_zone`}
            type={INPUT_TYPES.SELECT}
            placeholder="TMZ"
            value={template.time_zone}
            onChange={onChange}
            options={TIMEZONES}
            inputClassName="bg-almostWhite shadow-inner"
            className="w-24"
          />
          <RadioTabs>
            <RadioTabs.Tab
              id={`${templateIndex}.type.ALL`}
              name={`${templateIndex}.type.ALL`}
              checked={template.type === 'ALL'}
              onChange={(event) => onTemplateTypeChange(event, templateIndex)}
              label="ALL"
              value="ALL"
            />
            <RadioTabs.Tab
              id={`${templateIndex}.type.WEEKDAY_WEEKEND`}
              name={`${templateIndex}.type.WEEKDAY_WEEKEND`}
              checked={template.type === 'WEEKDAY_WEEKEND'}
              onChange={(event) => onTemplateTypeChange(event, templateIndex)}
              label="WEEKDAY - WEEKEND"
              value="WEEKDAY_WEEKEND"
            />
            <RadioTabs.Tab
              id={`${templateIndex}.type.CUSTOM`}
              name={`${templateIndex}.type.CUSTOM`}
              checked={template.type === 'CUSTOM'}
              onChange={(event) => onTemplateTypeChange(event, templateIndex)}
              label="CUSTOM"
              value="CUSTOM"
            />
          </RadioTabs>
        </div>
        <ul className="list-reset mt-5">
          {template.type === 'ALL' && (
            <TimeRow
              {...props}
              isFirstBracket
              rowLabel="All Days"
              templateType={template.type}
              inputPathPrefix={`${templateIndex}.opening_hour_times.0`}
              valueSource={template.opening_hour_times[0]}
            />
          )}
          {template.type === 'WEEKDAY_WEEKEND' && (
            <>
              <TimeRow
                {...props}
                isFirstBracket
                rowLabel="Weekday"
                templateType={template.type}
                inputPathPrefix={`${templateIndex}.opening_hour_times.0`}
                valueSource={template.opening_hour_times[0]}
              />
              <TimeRow
                {...props}
                rowLabel="Weekend"
                templateType={template.type}
                inputPathPrefix={`${templateIndex}.opening_hour_times.5`}
                valueSource={template.opening_hour_times[5]}
              />
            </>
          )}
          {template.type === 'CUSTOM' &&
            template.opening_hour_times.map((time, timeRowIndex) => {
              return (
                <TimeRow
                  {...props}
                  timeRowIndex={timeRowIndex}
                  isFirstBracket={timeRowIndex === 0}
                  key={time.id}
                  rowLabel={time.weekday}
                  templateType={template.type}
                  inputPathPrefix={`${templateIndex}.opening_hour_times.${timeRowIndex}`}
                  valueSource={template.opening_hour_times[timeRowIndex]}
                />
              );
            })}
        </ul>
      </div>
    </motion.li>
  );
};

OpeningHourCard.propTypes = {
  template: PropTypes.object.isRequired,
  templateIndex: PropTypes.number.isRequired,
  isDeleted: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  showDeleteWarningPopup: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.number,
  ]),
  toggleDeleteWarningPopup: PropTypes.func.isRequired,
  deleteTemplate: PropTypes.func.isRequired,
  onTemplateTypeChange: PropTypes.func.isRequired,
};

export default  OpeningHourCard;
